import { Icon, Typography } from '@components/style';
import { theme } from '@styles/theme';
import { useEffect, useRef, useState, type PropsWithChildren } from 'react';
import styled from 'styled-components';
import { useCheckDevice } from '../../../../hooks/UseCheckDevice';

type AccordionProps = {
  isOpen: boolean;
  placeholder: string;
  cssClass?: string;
  padding?: number;
};

const Accordion = ({
  isOpen,
  placeholder,
  children,
  cssClass,
  padding,
}: PropsWithChildren<AccordionProps>): JSX.Element => {
  const accordionLabelRef = useRef<HTMLSpanElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [accordionClass, setAccordionClass] = useState<string>('accordion-collapse');
  const { isMobileDevice } = useCheckDevice();

  const handleClick = (): void => {
    setIsVisible(!isVisible);
    accordionLabelRef?.current?.classList.toggle('accordion-clicked');
  };

  useEffect(() => {
    isVisible ? setAccordionClass('accordion-expand') : setAccordionClass('accordion-collapse');
  }, [isVisible]);

  useEffect(() => {
    if (isOpen) {
      handleClick();
    }
  }, [isOpen]);

  return (
    <Wrapper isMobileDevice={isMobileDevice} data-testid='accordion' className={accordionClass}>
      <Typography
        data-testid={`accordion-${placeholder}`}
        className='accordion-label'
        theme={theme}
        size={theme.typography.h5}
        fontWeight={theme.typography.fontWeights.semibold}
        onClick={handleClick}
        ref={accordionLabelRef}
      >
        <Icon
          type={isVisible ? 'angle-down' : 'angle-right'}
          additionalClassName='accordion-icon'
          size={20}
          padding={padding}
        />
        &nbsp;{placeholder}
      </Typography>
      {isVisible && (
        <div data-testid='accordion-content' className={`accordion-content ${cssClass}`}>
          {children}
        </div>
      )}
    </Wrapper>
  );
};

export default Accordion;
export type WrapperProps = {
  isMobileDevice?: boolean;
};

const Wrapper = styled.section<WrapperProps>`
  width: 100%;
  .accordion-label {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
  }
  .accordion-content.no-margin {
    border-radius: 5px;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);
    margin-top: 15px;
  }
  .accordion-content:not(.no-margin) {
    max-height: 0;
    padding: 0px;
    transition: all 0.3s;
    display: flex;
    flex-direction: column;
    margin-top: 5px;
    & > * {
      margin-top: ${(props) => (props.isMobileDevice ? '0' : '10')}px;
    }
  }
  .accordion-clicked {
    ~ .accordion-content {
      max-height: 180vh;
    }
  }
  padding-top: 10px;
  padding-bottom: 10px;
`;
